import React, { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Minus, Sparkles, Sun, IndianRupee, Leaf, MapPin } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { lookupPincode } from "@/lib/pincode";
import { useTranslation } from "@/context/LanguageContext";

const RESIDENTIAL_APPLIANCES = [
  { key: "fan", watts: 70, defaultHours: 8 },
  { key: "tube", watts: 20, defaultHours: 6 },
  { key: "ac", watts: 1500, defaultHours: 6 },
  { key: "fridge", watts: 150, defaultHours: 24 },
  { key: "tv", watts: 100, defaultHours: 5 },
  { key: "geyser", watts: 2000, defaultHours: 1 },
  { key: "washer", watts: 500, defaultHours: 1 },
  { key: "microwave", watts: 1200, defaultHours: 0.5 },
  { key: "computer", watts: 200, defaultHours: 6 },
  { key: "pump_home", watts: 750, defaultHours: 1 },
];

const AGRI_APPLIANCES = [
  { key: "pump_3hp", watts: 2200, defaultHours: 4 },
  { key: "pump_5hp", watts: 3700, defaultHours: 4 },
  { key: "pump_75hp", watts: 5500, defaultHours: 4 },
  { key: "pump_10hp", watts: 7500, defaultHours: 4 },
  { key: "lights", watts: 200, defaultHours: 10 },
  { key: "chaffcutter", watts: 1500, defaultHours: 2 },
];

const COMMERCIAL_APPLIANCES = [
  { key: "ac", watts: 1500, defaultHours: 9 },
  { key: "tube", watts: 20, defaultHours: 10 },
  { key: "computer", watts: 200, defaultHours: 9 },
  { key: "fridge", watts: 150, defaultHours: 24 },
  { key: "fan", watts: 70, defaultHours: 9 },
  { key: "pump_home", watts: 750, defaultHours: 2 },
  { key: "microwave", watts: 1200, defaultHours: 1 },
];

const TARIFF = 8;
const COM_TARIFF = 9.5;

function residentialSubsidy(kw) {
  if (kw <= 0) return 0;
  if (kw <= 1) return Math.round(30000 * kw);
  if (kw <= 2) return 30000 + Math.round(30000 * (kw - 1));
  if (kw <= 3) return 60000 + Math.round(18000 * (kw - 2));
  return 78000;
}

function recommendKw(monthlyUnits) {
  const kw = monthlyUnits / 120;
  return Math.max(1, Math.ceil(kw * 2) / 2);
}

export default function SavingsCalculator({ onRequestQuote }) {
  const { t } = useTranslation();
  const [segment, setSegment] = useState("residential");
  const [pincode, setPincode] = useState("");
  const detectedDiscom = React.useMemo(() => lookupPincode(pincode), [pincode]);
  const base =
    segment === "residential"
      ? RESIDENTIAL_APPLIANCES
      : segment === "agriculture"
      ? AGRI_APPLIANCES
      : COMMERCIAL_APPLIANCES;
  const tariff = segment === "commercial" ? COM_TARIFF : TARIFF;

  const [items, setItems] = useState(() =>
    Object.fromEntries(
      RESIDENTIAL_APPLIANCES.concat(AGRI_APPLIANCES, COMMERCIAL_APPLIANCES).map((a) => [
        a.key,
        { qty: 0, hours: a.defaultHours },
      ])
    )
  );

  React.useEffect(() => {
    setItems((prev) => {
      const copy = { ...prev };
      if (segment === "residential") {
        copy.fan = { ...copy.fan, qty: copy.fan.qty || 3 };
        copy.tube = { ...copy.tube, qty: copy.tube.qty || 6 };
        copy.fridge = { ...copy.fridge, qty: copy.fridge.qty || 1 };
        copy.tv = { ...copy.tv, qty: copy.tv.qty || 1 };
      } else if (segment === "agriculture") {
        copy.pump_5hp = { ...copy.pump_5hp, qty: copy.pump_5hp.qty || 1 };
      } else {
        copy.ac = { ...copy.ac, qty: copy.ac.qty || 4, hours: 9 };
        copy.tube = { ...copy.tube, qty: copy.tube.qty || 20, hours: 10 };
        copy.computer = { ...copy.computer, qty: copy.computer.qty || 6, hours: 9 };
        copy.fan = { ...copy.fan, qty: copy.fan.qty || 6, hours: 9 };
      }
      return copy;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segment]);

  const setQty = (key, delta) =>
    setItems((p) => ({
      ...p,
      [key]: { ...p[key], qty: Math.max(0, (p[key]?.qty || 0) + delta) },
    }));

  const setHours = (key, hours) =>
    setItems((p) => ({ ...p, [key]: { ...p[key], hours } }));

  const calc = useMemo(() => {
    let dailyWh = 0;
    for (const a of base) {
      const { qty = 0, hours = 0 } = items[a.key] || {};
      dailyWh += a.watts * qty * hours;
    }
    const dailyKwh = dailyWh / 1000;
    const monthlyUnits = dailyKwh * 30;
    const monthlyBill = monthlyUnits * tariff;
    const kw = monthlyUnits > 0 ? recommendKw(monthlyUnits) : 0;
    const perKwCost = segment === "commercial" ? 55000 : 60000;
    const systemCost = kw * perKwCost;
    let subsidy = 0;
    let subsidyLabel = "pm_subsidy";
    if (segment === "residential") {
      subsidy = residentialSubsidy(kw);
      subsidyLabel = "pm_subsidy";
    } else if (segment === "agriculture") {
      subsidy = Math.round(systemCost * 0.3);
      subsidyLabel = "kusum_subsidy";
    } else {
      // Commercial — Accelerated Depreciation tax benefit (~40% effective)
      subsidy = Math.round(systemCost * 0.4);
      subsidyLabel = "depreciation";
    }
    const netCost = Math.max(0, systemCost - subsidy);
    const annualGen = kw * 1460;
    const annualSavings = Math.min(annualGen, monthlyUnits * 12) * tariff;
    const monthlySavings = annualSavings / 12;
    const paybackYears = annualSavings > 0 ? netCost / annualSavings : 0;
    const co2Tonnes = (annualGen * 0.82) / 1000;
    return {
      dailyKwh, monthlyUnits, monthlyBill, kw, systemCost, subsidy, subsidyLabel, netCost,
      annualSavings, monthlySavings, paybackYears, co2Tonnes,
    };
  }, [items, base, segment, tariff]);

  const applianceLabel = (key) => t(`calc.appliance_labels.${key}`);

  return (
    <section
      id="calculator"
      data-testid="calculator-section"
      className="py-20 md:py-28 bg-white border-t border-stone-200"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div className="max-w-2xl">
            <span className="text-green-700 font-semibold tracking-widest text-xs uppercase">
              {t("calc.eyebrow")}
            </span>
            <h2 className="mt-3 font-display font-extrabold text-4xl lg:text-5xl text-stone-900">
              {t("calc.title_pre")}{" "}
              <span className="text-green-700">{t("calc.title_hl")}</span>{" "}
              {t("calc.title_post")}
            </h2>
            <p className="mt-3 text-stone-600">{t("calc.subtitle")}</p>
          </div>
          <Tabs value={segment} onValueChange={setSegment} data-testid="calc-segment-tabs">
            <TabsList className="bg-stone-100 rounded-full p-1">
              <TabsTrigger
                value="residential"
                data-testid="calc-tab-res"
                className="rounded-full px-4 py-2 text-sm data-[state=active]:bg-green-600 data-[state=active]:text-white"
              >
                {t("calc.tab_res")}
              </TabsTrigger>
              <TabsTrigger
                value="commercial"
                data-testid="calc-tab-com"
                className="rounded-full px-4 py-2 text-sm data-[state=active]:bg-orange-500 data-[state=active]:text-white"
              >
                {t("calc.tab_com")}
              </TabsTrigger>
              <TabsTrigger
                value="agriculture"
                data-testid="calc-tab-agri"
                className="rounded-full px-4 py-2 text-sm data-[state=active]:bg-green-600 data-[state=active]:text-white"
              >
                {t("calc.tab_agri")}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className="mt-10 grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 rounded-3xl border border-stone-200 bg-stone-50/50 p-5 md:p-7">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div className="font-display font-bold text-lg text-stone-900">
                {t("calc.appliances")}
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400" />
                  <Input
                    value={pincode}
                    onChange={(e) =>
                      setPincode(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    placeholder={t("calc.pincode_placeholder")}
                    inputMode="numeric"
                    data-testid="calc-pincode"
                    className="pl-8 h-9 w-[140px] rounded-full bg-white"
                  />
                </div>
                <div className="text-xs text-stone-500 whitespace-nowrap">
                  {t("calc.tariff", { rate: tariff })}
                </div>
              </div>
            </div>
            {detectedDiscom ? (
              <div
                className="mb-4 inline-flex items-center gap-1.5 text-xs font-medium text-green-800 bg-green-100 border border-green-200 rounded-full px-3 py-1"
                data-testid="calc-discom-detected"
              >
                <MapPin className="w-3 h-3" />
                {t("calc.detected")}: {detectedDiscom.state} · {detectedDiscom.discom}
              </div>
            ) : null}
            <div className="space-y-3">
              {base.map((a) => {
                const it = items[a.key] || { qty: 0, hours: a.defaultHours };
                const active = it.qty > 0;
                return (
                  <div
                    key={a.key}
                    data-testid={`appliance-row-${a.key}`}
                    className={`rounded-2xl border p-4 transition bg-white ${
                      active ? "border-green-300 shadow-sm" : "border-stone-200"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <div className="font-semibold text-stone-900 truncate">
                          {applianceLabel(a.key)}
                        </div>
                        <div className="text-xs text-stone-500 mt-0.5">
                          {a.watts} W · {t("calc.default_hours", { h: a.defaultHours })}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 bg-stone-100 rounded-full p-1">
                        <button
                          onClick={() => setQty(a.key, -1)}
                          data-testid={`appliance-${a.key}-minus`}
                          className="w-8 h-8 rounded-full bg-white border border-stone-200 grid place-items-center hover:bg-stone-50 disabled:opacity-40"
                          disabled={it.qty <= 0}
                          aria-label="Decrease"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <div
                          className="min-w-[28px] text-center font-display font-extrabold digit-stat text-stone-900"
                          data-testid={`appliance-${a.key}-qty`}
                        >
                          {it.qty}
                        </div>
                        <button
                          onClick={() => setQty(a.key, +1)}
                          data-testid={`appliance-${a.key}-plus`}
                          className="w-8 h-8 rounded-full bg-green-600 text-white grid place-items-center hover:bg-green-700"
                          aria-label="Increase"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    {active && (
                      <div className="mt-4 pt-4 border-t border-stone-100 flex items-center gap-4">
                        <div className="text-xs text-stone-500 w-20 shrink-0">
                          {t("calc.hours_day")}
                        </div>
                        <Slider
                          value={[it.hours]}
                          min={0}
                          max={24}
                          step={0.5}
                          onValueChange={(v) => setHours(a.key, v[0])}
                          className="flex-1"
                          data-testid={`appliance-${a.key}-hours`}
                        />
                        <div className="font-display font-bold text-green-700 digit-stat w-10 text-right">
                          {it.hours}h
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-2 relative">
            <div className="sticky top-24 rounded-3xl bg-green-800 text-white p-6 md:p-7 overflow-hidden shadow-xl">
              <div className="absolute -top-12 -right-12 w-56 h-56 rounded-full bg-yellow-400/20 blur-2xl" />
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest opacity-80">
                <Sparkles className="w-4 h-4 text-yellow-300" /> {t("calc.snapshot")}
              </div>

              <div className="mt-5 grid grid-cols-2 gap-3">
                <Stat label={t("calc.daily")} value={`${calc.dailyKwh.toFixed(1)} kWh`} />
                <Stat label={t("calc.monthly_units")} value={`${Math.round(calc.monthlyUnits)}`} />
                <Stat label={t("calc.current_bill")} value={`₹${Math.round(calc.monthlyBill).toLocaleString("en-IN")}`} />
                <Stat label={t("calc.recommended")} value={`${calc.kw.toFixed(1)} kW`} highlight />
              </div>

              <div className="mt-6 bg-white/10 backdrop-blur rounded-2xl p-5">
                <div className="text-[11px] uppercase tracking-widest opacity-75">
                  {t("calc.investment")}
                </div>
                <Row k={t("calc.system_cost")} v={inr(calc.systemCost)} />
                <Row k={t(`calc.${calc.subsidyLabel}`)} v={`– ${inr(calc.subsidy)}`} accent />
                <div className="h-px bg-white/20 my-2" />
                <Row k={t("calc.net_investment")} v={inr(calc.netCost)} bold />
              </div>

              {segment === "commercial" ? (
                <div className="mt-3 text-[11px] bg-orange-400/20 border border-orange-400/40 rounded-xl p-3 text-orange-100">
                  {t("calc.com_note")}
                </div>
              ) : null}

              <div className="mt-5 grid grid-cols-3 gap-2">
                <MiniStat icon={IndianRupee} label={t("calc.monthly_savings")} value={inr(Math.round(calc.monthlySavings))} />
                <MiniStat icon={Sun} label={t("calc.payback")} value={calc.paybackYears > 0 ? `${calc.paybackYears.toFixed(1)} yr` : "—"} />
                <MiniStat icon={Leaf} label={t("calc.co2_yr")} value={`${calc.co2Tonnes.toFixed(1)} t`} />
              </div>

              <Button
                onClick={() =>
                  onRequestQuote({
                    estimated_kw: calc.kw,
                    monthly_units: Math.round(calc.monthlyUnits),
                    monthly_bill: Math.round(calc.monthlyBill),
                    segment,
                    pincode: pincode || null,
                    state: detectedDiscom?.state || null,
                    discom: detectedDiscom?.discom || null,
                  })
                }
                data-testid="calc-cta-quote"
                className="w-full mt-6 rounded-full bg-yellow-400 hover:bg-yellow-300 text-stone-900 h-12 font-bold shadow-lg hover:-translate-y-[2px] transition"
              >
                {t("calc.cta_quote", { kw: calc.kw.toFixed(1) })}
              </Button>
              <p className="text-[11px] opacity-75 mt-3 text-center">
                {t("calc.disclaimer", { rate: tariff })}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div
      className={`rounded-2xl p-3 ${highlight ? "bg-yellow-400 text-stone-900" : "bg-white/10"}`}
    >
      <div className={`text-[11px] uppercase tracking-wider ${highlight ? "text-stone-800" : "opacity-75"}`}>
        {label}
      </div>
      <div className="font-display font-extrabold text-xl digit-stat">{value}</div>
    </div>
  );
}

function Row({ k, v, accent, bold }) {
  return (
    <div className="flex items-center justify-between mt-2 text-sm">
      <span className="opacity-85">{k}</span>
      <span
        className={`${accent ? "text-yellow-300" : ""} ${
          bold ? "font-display font-extrabold text-lg" : "font-semibold"
        } digit-stat`}
      >
        {v}
      </span>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl bg-white/10 p-3">
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider opacity-80">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="mt-1 font-display font-bold text-[15px] digit-stat">{value}</div>
    </div>
  );
}

function inr(n) {
  return `₹${Math.round(n).toLocaleString("en-IN")}`;
}
